import { useEffect, useState } from 'react';

import {
  InjectedFieldProps,
  PublicFieldProps,
  useDataProvider,
  useNotify,
} from 'react-admin';
import Box from '@material-ui/core/Box';
import MUIButton from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import GeoStatusAlert from 'components/GeoStatusAlert';
import {
  BumpResourceSheetDto,
  JobPhaseBumpApprovalStatus,
} from '@vatos-pas/common';

import { Show } from 'components/Show';
import {
  CAN_APPROVE_BUMPOUT,
  CAN_MANAGE_HO_SERVICE_REPAIR,
} from 'providers/permissions';
import {
  canEditHOServiceRepair,
  isConfirmOnlyRepair,
  isJobPhaseBumpType,
  isPendingSupervisorStatus,
} from '../utils';
import { isApprovedStatus } from 'utils/bumpApproval';
import { LocationProvider } from 'providers/locationProvider';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { BumpoutTimesheetFields } from '../views/bumpout-timesheet-list';
import { DateTime } from 'luxon';

type ButtonProps = {
  fields: BumpoutTimesheetFields | null;
  handleRefetch: () => void;
} & PublicFieldProps &
  InjectedFieldProps<BumpResourceSheetDto>;

const Button = ({ record, fields, handleRefetch }: ButtonProps) => {
  if (!record?.id) return null;

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const {
    coords: geoCoords,
    accuracy: geoAccuracy,
    error: geoError,
    loading: geoLoading,
    requestLocation,
  } = LocationProvider.useContainer();
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (isDialogOpen) {
      requestLocation();
    }
  }, [isDialogOpen, requestLocation]);

  const isJobPhaseBump = isJobPhaseBumpType(record.bumpResourceType);
  const isConfirmOnlyPendingApproval =
    isConfirmOnlyRepair(record.repairType, record.estimatedHours) &&
    record.bumperApprovalStatus === JobPhaseBumpApprovalStatus.PENDING_APPROVAL;
  const requiresHoursValidation =
    !isJobPhaseBump && !isConfirmOnlyPendingApproval;
  const currentFields = fields?.[record.id];

  const handleButtonClick = () => {
    if (geoCoords) {
      handleApprove();
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleApprove = async () => {
    try {
      setIsLoading(true);

      const data: Record<string, unknown> = {
        isApprovedHours: true,
        ...(isJobPhaseBump && !currentFields?.workedHours
          ? { approvedHours: 0 }
          : currentFields?.workedHours
          ? { approvedHours: +currentFields.workedHours }
          : {}),
      };

      if (geoCoords) {
        data.approvedGeo = {
          type: 'Point',
          coordinates: [geoCoords[0], geoCoords[1]],
        };
        data.approvedGeoAccuracy = geoAccuracy;
      }

      await dataProvider.update(
        isJobPhaseBump ? '/job-phase-bump' : '/repair-payment',
        {
          id: record.id,
          data,
          previousData: record,
        },
      );

      notify('Worked hours approved.');
      handleRefetch();
    } catch (error: any) {
      notify(error.message || 'Failed to approve worked hours', 'warning');
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  };

  const numberValue =
    typeof currentFields?.workedHours === 'string'
      ? parseFloat(currentFields.workedHours)
      : null;

  const timeIn =
    currentFields?.timeIn instanceof DateTime &&
    currentFields.timeIn.toFormat('HH:mm:00');

  const timeOut =
    currentFields?.timeOut instanceof DateTime &&
    currentFields.timeOut.toFormat('HH:mm:00');

  const diffTimeIn = timeIn && timeIn !== record.timeIn;
  const diffTimeOut = timeOut && timeOut !== record.timeOut;

  return (
    <>
      <MUIButton
        variant="contained"
        color="primary"
        disabled={
          isLoading ||
          (requiresHoursValidation &&
            (diffTimeIn ||
              diffTimeOut ||
              numberValue === null ||
              isNaN(numberValue) ||
              numberValue <= 0))
        }
        onClick={handleButtonClick}
      >
        Approve
      </MUIButton>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="approve-geo-confirm-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          id="approve-geo-confirm-dialog-title"
          className={classes.contentTitle}
        >
          Approve - Worked Hours
        </DialogTitle>
        <DialogContent>
          <GeoStatusAlert
            loading={geoLoading}
            error={geoError}
            coords={geoCoords}
            action="approving"
          />
        </DialogContent>
        <DialogActions>
          <Box width="100%" display="flex" justifyContent="space-between">
            <MUIButton
              onClick={() => setIsDialogOpen(false)}
              className={classes.cancelButton}
            >
              CANCEL
            </MUIButton>
            <MUIButton
              onClick={handleApprove}
              color="primary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : 'APPROVE'}
            </MUIButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

type ApproveButtonProps = {
  fields: BumpoutTimesheetFields | null;
  handleRefetch: () => void;
} & PublicFieldProps &
  InjectedFieldProps<BumpResourceSheetDto>;

const ApproveButton = ({
  record,
  fields,
  handleRefetch,
}: ApproveButtonProps) => {
  if (!record?.id) return null;

  const { hasPermission } = PermissionsProvider.useContainer();

  const canApprove = hasPermission(CAN_APPROVE_BUMPOUT);
  const canManageHOService = hasPermission(CAN_MANAGE_HO_SERVICE_REPAIR);
  const isBumpoutApproved = isApprovedStatus(record?.bumperApprovalStatus);
  const isJobPhaseBump = isJobPhaseBumpType(record?.bumpResourceType);

  const isPendingSupervisorAction =
    isJobPhaseBump || isPendingSupervisorStatus(record?.bumperApprovalStatus);

  if (isBumpoutApproved) return null;

  return (
    <Show
      condition={
        canApprove &&
        isPendingSupervisorAction &&
        canEditHOServiceRepair(record?.repairType, canManageHOService)
      }
    >
      <Button fields={fields} record={record} handleRefetch={handleRefetch} />
    </Show>
  );
};

const useStyles = makeStyles(theme => ({
  contentTitle: {
    fontWeight: 'bold',
  },
  cancelButton: {
    color: theme.palette.error.main,
  },
}));

export default ApproveButton;
